import { useContext, useEffect, useState } from 'react';
import Tab from '../../components/Tab';
import { useAppSelector, useAppDispatch } from '../../states/hooks';
import { fetchGetAuthenticated } from 'services/auth.service';
import { Box } from '@mui/material';
import Layout from 'layouts/Layout';
import {
	ClientContainer,
	UnifiedDashboardCtx,
} from 'packages/unified-dashboard/unified-dashboard.provider';
import { BACKEND_URL } from 'config';
import { enqueueSnackbar } from 'notistack';
import { LocalStorageService } from 'services/localStorage.service';
import { logout } from 'states/userSlice';
import { useLocation, useNavigate } from 'react-router-dom';

export type RoleResponseType = {
	id?: number;
	key: string;
	label: string;
	dashboardTitle: string;
};
export interface SidebarItemType {
	onClick: () => void;
	label: string;
	icon?: string;
	activeUrl?: string[];
}

export const EMPTY_DEALERSHIP_ERROR = '[E_EmptyDealershipList]';

const UnifiedHome = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const ctx = useContext(UnifiedDashboardCtx);
	const { userAuth } = useAppSelector((state) => state.user);

	const [selectedRole, setSelectedRole] = useState<RoleResponseType>({
		id: undefined,
		label: '',
		key: '',
		dashboardTitle: '',
	});
	const [sidebarItems, setSidebarItems] = useState<SidebarItemType[]>([]);

	useEffect(() => {
		if (!userAuth) return;

		const logoutUP = () => {
			dispatch(logout());
			// clearAuthData();
			ctx.dispatch({ type: 'UNIFIED_DASHBOARD_ACTION_LOGOUT' });
			LocalStorageService.clearAll();
			LocalStorageService.clearAuthV2();
			enqueueSnackbar('Account logged out', {
				variant: 'success',
			});
			navigate('/login');
		};

		const fetchRoles = async () => {
			// check role permission
			const res = await fetchGetAuthenticated({
				route: '/v1/dashboard/roles',
				token: userAuth,
				dispatch,
			});
			const mappedRoles: RoleResponseType[] = res.data.roles.map(
				(role: RoleResponseType) => ({
					id: role.id || -1,
					key: role?.key,
					label: role?.label,
					dashboardTitle: role?.dashboardTitle || '',
				}),
			);
			setSelectedRole(mappedRoles[0]);

			// check tableau access
			const newSidebarItems: SidebarItemType[] = [];

			const responseSales = await fetch(
				`${BACKEND_URL}/api/v1/tableau/html/by-role`,
				{
					method: 'POST',
					body: JSON.stringify({
						filters: {},
						roleId: mappedRoles[0].id ?? -1,
						roleType: mappedRoles[0].key,
					}),
					headers: {
						Authorization: `Bearer ${userAuth || ctx.state.mftAccessToken}`,
					},
				},
			);
			const responseAfterSales = await fetch(
				`${BACKEND_URL}/api/v1/tableau/alps/html/by-role`,
				{
					method: 'POST',
					body: JSON.stringify({
						filters: {},
						roleId: mappedRoles[0].id ?? -1,
						roleType: mappedRoles[0].key,
					}),
					headers: {
						Authorization: `Bearer ${userAuth || ctx.state.mftAccessToken}`,
					},
				},
			);

			if (responseSales.ok) {
				newSidebarItems.push({
					onClick: () => {
						navigate('/sales');
					},
					label: 'Sales',
					icon: `/images/sidebar/icon-chart-active.png`,
					activeUrl: ['/sales'],
				});
			}
			if (responseAfterSales.ok) {
				newSidebarItems.push({
					onClick: () => {
						navigate('/after-sales');
					},
					label: 'After-Sales',
					icon: `/images/sidebar/icon-chart-active.png`,
					activeUrl: ['/after-sales'],
				});
			}

			if (!newSidebarItems.length) {
				logoutUP();
				return;
			}
			setSidebarItems(newSidebarItems);
			const newUrl = newSidebarItems?.[0]?.activeUrl?.[0];
			if (newUrl) navigate(newUrl);
		};
		fetchRoles();
	}, [userAuth]);

	const roleData = {
		roleId: selectedRole.id ?? -1,
		roleType: selectedRole.key,
	};

	return (
		<ClientContainer>
			<Layout sidebarItems={sidebarItems}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
						height: '100%',
					}}
				>
					<Box
						className="self-center"
						style={{ marginBottom: '80px', width: '100%' }}
					>
						{sidebarItems.length ? (
							<Tab roleData={roleData} location={location} />
						) : null}
					</Box>
				</Box>
			</Layout>
		</ClientContainer>
	);
};

export default UnifiedHome;
